import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
} from "@tanstack/react-table";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useReactTableNoMemo } from "#shared/helpers/useReactTableNoMemo";
import { Icon } from "#shared/icons/Icon";
import { ActionIcon } from "#shared/view/components/ActionIcon";
import { Group } from "#shared/view/components/Group";
import { Pagination } from "#shared/view/components/Pagination";
import { Stack } from "#shared/view/components/Stack";
import { Button } from "#shared/view/designSystem/Button";
import { Table } from "#shared/view/designSystem/Table";
import { CompanySurfaceTreatment, formatPrice, SurfaceTreatmentMethodSchema } from "shared";

import {
  getSurfaceTreatmentMethodLabel,
  getSurfaceTreatmentPriceSuffix,
  getSurfaceTreatmentTypeLabel,
} from "./surfaceTreatmentLabels";

const PAGE_SIZE = 15;

type SurfaceTreatmentsSectionTableProps = {
  data: CompanySurfaceTreatment[];
  onAddRow: () => void;
  onEditRow: (rowId: string) => void;
  onRemoveRow: (rowId: string) => void;
};

type SurfaceTreatmentWithIndex = CompanySurfaceTreatment & {
  originalIndex: number;
};

export const SurfaceTreatmentsSectionTable = ({
  data,
  onAddRow,
  onEditRow,
  onRemoveRow,
}: SurfaceTreatmentsSectionTableProps) => {
  const { t } = useTranslation();

  const columns = useMemo<ColumnDef<SurfaceTreatmentWithIndex>[]>(
    () => [
      {
        accessorKey: "description",
        header: t("settings.surfaceTreatments.description"),
        meta: { width: "20%" },
      },
      {
        accessorKey: "type",
        header: t("settings.surfaceTreatments.type"),
        meta: { width: "10%" },
        cell: ({ row }) => getSurfaceTreatmentTypeLabel(t, row.original.type),
      },
      {
        accessorKey: "method",
        header: t("settings.surfaceTreatments.method"),
        meta: { width: "12%" },
        cell: ({ row }) => getSurfaceTreatmentMethodLabel(t, row.original.method),
      },
      {
        accessorKey: "price",
        header: t("settings.surfaceTreatments.price"),
        meta: { width: "12%" },
        cell: ({ row }) => {
          const { method } = row.original;
          if (method === SurfaceTreatmentMethodSchema.enum.formula) {
            return "-";
          }
          return formatPrice(row.original.price, getSurfaceTreatmentPriceSuffix(t, method));
        },
      },
      {
        accessorKey: "formula",
        header: t("settings.surfaceTreatments.formula"),
        meta: { width: "20%" },
        cell: ({ row }) => {
          const { method } = row.original;
          return method === SurfaceTreatmentMethodSchema.enum.formula
            ? (row.original.formula ?? "")
            : "-";
        },
      },
      {
        accessorKey: "preparationTimeFormula",
        header: t("settings.surfaceTreatments.preparationTimeFormula"),
        meta: { width: "10%" },
        cell: ({ row }) => {
          const { method } = row.original;
          return method === SurfaceTreatmentMethodSchema.enum.time
            ? (row.original.preparationTimeFormula ?? "")
            : "-";
        },
      },
      {
        accessorKey: "cycleTimeFormula",
        header: t("settings.surfaceTreatments.cycleTimeFormula"),
        meta: { width: "10%" },
        cell: ({ row }) => {
          const { method } = row.original;
          return method === SurfaceTreatmentMethodSchema.enum.time
            ? (row.original.cycleTimeFormula ?? "")
            : "-";
        },
      },
      {
        id: "actions",
        header: "",
        meta: { width: "6%" },
        cell: ({ row }) => (
          <Group gap="xs" justify="end" wrap="nowrap">
            <ActionIcon
              onClick={() => onEditRow(row.id)}
              variant="subtle"
              size="sm"
              aria-label={`Edit row ${row.index}`}
            >
              <Icon name="edit" size={14} />
            </ActionIcon>
            <ActionIcon
              onClick={() => onRemoveRow(row.id)}
              variant="subtle"
              size="sm"
              aria-label={`Remove row ${row.index}`}
            >
              <Icon name="trash" size={14} />
            </ActionIcon>
          </Group>
        ),
      },
    ],
    [onEditRow, onRemoveRow, t],
  );

  const sortedData = useMemo<SurfaceTreatmentWithIndex[]>(() => {
    return data
      .map((item, originalIndex) => ({ ...item, originalIndex }))
      .sort((a, b) => (a.description || "").localeCompare(b.description || ""));
  }, [data]);

  const { table, rows, headerGroups, leafColumns, pageCount, state } = useReactTableNoMemo({
    data: sortedData,
    columns,
    getRowId: (row) => row.id,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize: PAGE_SIZE, pageIndex: 0 } },
    autoResetPageIndex: false,
  });

  return (
    <Stack gap="sm">
      <Table highlightOnHover stickyHeader style={{ tableLayout: "fixed" }}>
        <colgroup>
          {leafColumns.map((col) => {
            const columnsCount = leafColumns.length;
            const defaultWidth = 100 / columnsCount;
            const w =
              (col.columnDef.meta as { width?: string | number } | undefined)?.width ??
              `${defaultWidth}%`;
            return (
              <col key={col.id} style={{ width: typeof w === "number" ? `${w}px` : String(w) }} />
            );
          })}
        </colgroup>
        <Table.Thead>
          {headerGroups.map((headerGroup) => (
            <Table.Tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <Table.Th key={header.id}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext())}
                </Table.Th>
              ))}
            </Table.Tr>
          ))}
        </Table.Thead>
        <Table.Tbody>
          {rows.map((row) => (
            <Table.Tr key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <Table.Td key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </Table.Td>
              ))}
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>

      <Group justify="space-between">
        <Button onClick={onAddRow} variant="outline" leftSection={<Icon name="plus" />}>
          {t("common.add")}
        </Button>
        {pageCount > 1 && (
          <Pagination
            total={pageCount}
            value={state.pagination.pageIndex + 1}
            onChange={(page) => table.setPageIndex(page - 1)}
          />
        )}
      </Group>
    </Stack>
  );
};
